
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { MountedAware } from '@/util/lifecycle';
import StandardCell from '@/ui/StandardCell.vue';
import LargeTextCell from '@/ui/LargeTextCell.vue';
import Image from '@/api/mjegb/Image';
import { layoutMappings } from '@/util/book/layouts';
import { PAGE_DEFAULT_FONT_SIZE } from '@/container';
import { TextConfiguration } from '@/ui/BookBuilder.vue';

@Component({
    components: {
        LargeTextCell,
        StandardCell,
    },
})
export default class PageEditor extends Vue implements MountedAware {
    @Prop({ type: String, required: true })
    public readonly paperType!: string;

    @Prop({ type: String, required: true })
    public readonly orientation!: string;

    @Prop({ type: String, required: true })
    public readonly layout!: string;

    @Prop({ type: String, required: true })
    public readonly fontType!: string;

    @Prop({ type: Number, required: true })
    public readonly fontSize!: number;

    @Prop({ type: Number, required: true })
    public readonly index!: number;

    @Prop({ type: String, required: true })
    public readonly heading!: string;

    @Prop({ type: Object, required: true })
    public readonly imageCellCache!: { [key: string]: Image };

    @Prop({ type: Object, required: true })
    public readonly textCellCache!: TextConfiguration;

    public internalHeading = '';
    public editingHeading = false;

    public get rows(): number[] {
        const limit = this.layoutMapping[this.layout]?.rows || 0;
        return [0, 1, 2].filter((t) => t < limit);
    }

    public get columns(): number[] {
        const limit = this.layoutMapping[this.layout]?.columns || 0;
        return [0, 1, 2].filter((t) => t < limit);
    }

    public get isOneLargeBlock(): boolean {
        return this.paperType === 'a4' && (this.layout === '1x1' || this.layout === '1x1+t');
    }

    public get hasText(): boolean {
        return this.layoutMapping[this.layout]?.text || false;
    }

    public get pageClasses(): any {
        const frameClass = 'mjegb-page-frame-' + this.orientation + '-' + this.paperType;
        const fontClass = 'mjegb-page-font-family-' + this.fontType;
        return {
            [frameClass]: true,
            [fontClass]: true,
        };
    }

    public get largeTextContainerStyle(): string {
        return 'font-size: ' + this.fontSize + 'px;';
    }

    private layoutMapping = layoutMappings;

    public mounted(): void {
        const pageRef: any = this.$refs.page;
        if (pageRef) {
            pageRef.style.fontSize = '' + PAGE_DEFAULT_FONT_SIZE + 'px';
        }
    }

    public onImageCellCacheUpdate(key: string, value: Image | null): void {
        this.$emit('image-cell-cache-update', { key, value });
    }

    public onTextCellCacheUpdate(value: TextConfiguration): void {
        this.$emit('text-cell-cache-update', value);
    }

    @Watch('heading', { immediate: true })
    public onHeadingChanged(heading: string): void {
        if (heading !== this.internalHeading) {
            this.internalHeading = heading;
        }
    }

    @Watch('editingHeading')
    public onEditingHeadingChanged(editing: boolean): void {
        if (!editing) {
            this.$emit('heading-changed', this.internalHeading);
        }
    }
}
